// ===========================================================================
// What Lumi charges in, and how a price is written.
//
// This existed in two places that did not know about each other: the server
// always charged `currency: 'usd'`, and the billing panel always printed a
// hardcoded `$` with a template string. Neither read a setting. Nothing would
// have noticed if one had been changed and the other had not -- and the first
// symptom of that is somebody being charged in a currency the page did not
// quote them in, which is the worst class of bug this codebase can have.
//
// So: one constant, imported by both, and one formatter that derives the
// symbol from it rather than assuming. Changing what Lumi charges in is a
// one-line change here and nowhere else.
//
// Deliberately NOT an environment variable. The server could read one; the
// browser cannot, and a price the page prints must be the price the checkout
// charges. A value both sides compile against cannot disagree.
// ===========================================================================

/** ISO 4217, lower case -- the casing Stripe's API wants. */
export const CURRENCY = 'usd';

/** The locale prices are written in when the caller does not name one. */
export const PRICE_LOCALE = 'en-US';

/**
 * Write an amount in minor units (cents) as a price.
 *
 * Intl derives the symbol from the currency, so this says $4.99 today and the
 * right thing on the day CURRENCY changes, instead of a `$` that has to be
 * hunted down. Falls back to a plain decimal on a runtime without Intl rather
 * than throwing inside a shop.
 *
 * @param {number} cents amount in minor units
 * @param {{ currency?: string, locale?: string }} [opts]
 * @returns {string}
 */
export function formatMoney(cents, { currency = CURRENCY, locale = PRICE_LOCALE } = {}) {
  const amount = Number(cents) || 0;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: String(currency).toUpperCase(),
    }).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} ${String(currency).toUpperCase()}`;
  }
}

export default { CURRENCY, PRICE_LOCALE, formatMoney };
